// Copyright (c) StrangeBee 2023

import { useLocalMessage } from '@Helpers/locale';
import React, { ReactElement } from 'react';
import { useCallback } from 'react';
import { useState } from 'react';
import { Comment } from 'antd';
import { ICONS } from '@Constants/iconsApp';
import DeleteButton from '@Atoms/Buttons/DeleteButton/DeleteButton';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import { FormComment } from './FormComment';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';

import './Comments.scss';

export interface MessageProps {
    objectId: string;
    user: string;
    date: string;
    message: string;
    avatar?: string;
    onEdit?: (text: string, id: string) => void;
    onDelete?: (id: string) => void;
    readOnly?: boolean;
    isMine?: boolean;
    edited?: string;
}

const Message = ({
    objectId,
    user,
    avatar = '',
    date,
    message,
    onEdit,
    onDelete,
    readOnly = true,
    isMine = false,
    edited,
}: MessageProps) => {
    const local = useLocalMessage();

    const [editMode, setEditMode] = useState(false);
    const [content, setContent] = useState(message);

    const onSubmitHandler = (txt: string) => {
        onEdit?.(txt, objectId);
        setContent(txt);
        setEditMode(false);
    };

    const onToggle = () => {
        setEditMode((prev) => !prev);
    };

    const onDeleteHandle = () => {
        onDelete?.(objectId);
    };

    const actions = [
        <THTooltip key="comment-basic-edit" title="Edit">
            <span className="comment-action" onClick={onToggle}>
                {<ICONS.EditIcon />}
            </span>
        </THTooltip>,
        <THTooltip key="comment-basic-dislike" title="Remove">
            <span className="comment-action">
                {
                    <DeleteButton
                        label={local('comment.approvedCancel')}
                        className="delete-button"
                        onClick={(event): void => event.stopPropagation()}
                        onConfirm={onDeleteHandle}
                    />
                }
            </span>
        </THTooltip>,
    ];

    return (
        <Comment
            actions={isMine && !readOnly ? actions : []}
            author={<>{user}</>}
            avatar={<UserAvatar size="small" avatar={avatar} name={user} />}
            content={
                editMode && !readOnly && isMine ? (
                    <FormComment onSubmit={onSubmitHandler} onReset={onToggle} message={content} />
                ) : (
                    <p className="content__text">{content}</p>
                )
            }
            datetime={
                <THTooltip title={date}>
                    <span>{date}</span>
                    {edited ? <span className="info__date"> - {local('comment.edited')}</span> : null}
                </THTooltip>
            }
        />
    );
};

interface CommentsProps {
    data: MessageProps[];
    showMore?: boolean;
    onEdit?: (text: string, id: string) => void;
    onDelete?: (id: string) => void;
    moreConfig?: {
        message?: string;
        onClick?: () => void;
        show?: boolean;
    };
}

export const Comments = ({ data, onEdit, onDelete, moreConfig }: CommentsProps): ReactElement => {
    const onClickHandler = useCallback(() => {
        moreConfig?.onClick?.();
    }, [moreConfig]);

    return (
        <>
            {moreConfig?.onClick && moreConfig?.show ? (
                <div
                    className="comment-more"
                    onClick={onClickHandler}
                    style={{ ['--comment-msg-more' as any]: `"${moreConfig?.message}"` }}
                />
            ) : null}
            <div className="comment">
                <div className="comment-list">
                    {data.map((info: any) => (
                        <Message key={info.objectId} {...info} onEdit={onEdit} onDelete={onDelete} />
                    ))}
                </div>
            </div>
        </>
    );
};
